/**
 * edj-wishlist.js
 * Wishlist anonyme (localStorage only) — Lot 6 EDJ Shopify theme
 *
 * Décision Guillaume 2026-05-02 : pas d'App Proxy, pas d'auth, pas de sync.
 * Persiste juste dans le navigateur de l'utilisateur.
 *
 * - localStorage key : edj_wishlist_v1
 * - Format : Array<{ handle, variant_id, title, image, price_cents, added_at }>
 * - Émet `wishlist:updated` sur `document` à chaque modification.
 *
 * Custom elements :
 *   <edj-wishlist-heart data-handle="..." data-variant-id="..." data-title="..." data-image="..." data-price-cents="...">
 *   <edj-wishlist-count>
 *   <edj-favoris-list>
 */

(function () {
  'use strict';

  const STORAGE_KEY = 'edj_wishlist_v1';
  const EVENT_NAME = 'wishlist:updated';

  /** Lit le tableau localStorage, gère parse error gracefully. */
  function readStorage() {
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) return [];
      const data = JSON.parse(raw);
      if (!Array.isArray(data)) return [];
      return data;
    } catch (e) {
      console.warn('[EdjWishlist] read error', e);
      return [];
    }
  }

  /** Écrit le tableau et notifie. */
  function writeStorage(items) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
      document.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: { items: items, count: items.length } }));
    } catch (e) {
      console.warn('[EdjWishlist] write error', e);
    }
  }

  function normHandle(handle) {
    return (handle || '').toString().trim();
  }

  /** Formate un prix (en cents Shopify) en EUR FR. */
  function formatPriceCents(cents) {
    const n = parseInt(cents, 10) || 0;
    const euros = n / 100;
    if (Math.round(euros) === euros) {
      return euros.toLocaleString('fr-FR') + ' €';
    }
    return euros.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
  }

  const EdjWishlist = {
    getAll() {
      return readStorage();
    },

    has(handle) {
      const h = normHandle(handle);
      if (!h) return false;
      return readStorage().some(function (it) { return it.handle === h; });
    },

    count() {
      return readStorage().length;
    },

    add(handle, variantId, productData) {
      const h = normHandle(handle);
      if (!h) return false;
      const items = readStorage();
      if (items.some(function (it) { return it.handle === h; })) return false;
      const data = productData || {};
      items.unshift({
        handle: h,
        variant_id: variantId ? variantId.toString() : '',
        title: data.title || '',
        image: data.image || '',
        price_cents: typeof data.price_cents === 'number' ? data.price_cents : (parseInt(data.price_cents, 10) || 0),
        added_at: Date.now()
      });
      writeStorage(items);
      return true;
    },

    remove(handle) {
      const h = normHandle(handle);
      if (!h) return false;
      const items = readStorage();
      const next = items.filter(function (it) { return it.handle !== h; });
      if (next.length === items.length) return false;
      writeStorage(next);
      return true;
    },

    toggle(handle, variantId, productData) {
      if (this.has(handle)) {
        this.remove(handle);
        return false;
      }
      this.add(handle, variantId, productData);
      return true;
    },

    clear() {
      writeStorage([]);
    }
  };

  window.EdjWishlist = EdjWishlist;

  // ─── <edj-wishlist-heart> ───────────────────────────────────────
  class EdjWishlistHeart extends HTMLElement {
    constructor() {
      super();
      this._onClick = this._onClick.bind(this);
      this._onUpdate = this._onUpdate.bind(this);
    }

    connectedCallback() {
      if (!this._rendered) {
        this._render();
        this._rendered = true;
      }
      this._refresh();
      this.addEventListener('click', this._onClick);
      document.addEventListener(EVENT_NAME, this._onUpdate);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this._onClick);
      document.removeEventListener(EVENT_NAME, this._onUpdate);
    }

    _render() {
      let btn = this.querySelector('button[data-edj-heart-btn]');
      if (!btn) {
        const innerBtn = this.querySelector('button');
        if (innerBtn) {
          innerBtn.classList.add('edj-heart__btn');
          innerBtn.setAttribute('data-edj-heart-btn', '');
          innerBtn.type = 'button';
          // Récupère le label texte existant pour le préserver dans __label
          const existingText = innerBtn.textContent.replace(/[♡♥]/g, '').trim();
          // Vide et reconstruit avec icon + label
          while (innerBtn.firstChild) innerBtn.removeChild(innerBtn.firstChild);
          const ic = document.createElement('span');
          ic.className = 'edj-heart__icon';
          ic.setAttribute('aria-hidden', 'true');
          ic.textContent = '♡';
          innerBtn.appendChild(ic);
          if (existingText) {
            const lab = document.createElement('span');
            lab.className = 'edj-heart__label';
            lab.textContent = existingText;
            innerBtn.appendChild(lab);
          }
          btn = innerBtn;
        } else {
          btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'edj-heart__btn';
          btn.setAttribute('data-edj-heart-btn', '');
          const ic = document.createElement('span');
          ic.className = 'edj-heart__icon';
          ic.setAttribute('aria-hidden', 'true');
          ic.textContent = '♡';
          btn.appendChild(ic);
          const lab = document.createElement('span');
          lab.className = 'edj-heart__label visually-hidden';
          lab.textContent = 'Ajouter aux favoris';
          btn.appendChild(lab);
          this.appendChild(btn);
        }
      }
      this._btn = btn;
      this._iconEl = btn.querySelector('.edj-heart__icon');
      this._labelEl = btn.querySelector('.edj-heart__label');
    }

    _onClick(e) {
      e.preventDefault();
      e.stopPropagation();
      const handle = this.dataset.handle || '';
      if (!handle) return;
      const variantId = this.dataset.variantId || '';
      const data = {
        title: this.dataset.title || '',
        image: this.dataset.image || '',
        price_cents: parseInt(this.dataset.priceCents || '0', 10) || 0
      };
      const nowOn = EdjWishlist.toggle(handle, variantId, data);
      this._refresh();
      if (this._iconEl && nowOn) {
        this._iconEl.classList.remove('is-pulse');
        void this._iconEl.offsetWidth;
        this._iconEl.classList.add('is-pulse');
      }
    }

    _onUpdate() {
      this._refresh();
    }

    _refresh() {
      if (!this._btn) return;
      const handle = this.dataset.handle || '';
      const on = handle ? EdjWishlist.has(handle) : false;
      this._btn.setAttribute('aria-pressed', on ? 'true' : 'false');
      this._btn.setAttribute('aria-label', on ? 'Retirer des favoris' : 'Ajouter aux favoris');
      this.classList.toggle('is-on', on);
      if (this._iconEl) {
        this._iconEl.textContent = on ? '♥' : '♡';
      }
      if (this._labelEl && this.dataset.labelText !== undefined) {
        this._labelEl.textContent = on
          ? (this.dataset.labelTextOn || 'Retirer des favoris')
          : (this.dataset.labelText || 'Ajouter aux favoris');
      }
    }
  }
  customElements.define('edj-wishlist-heart', EdjWishlistHeart);

  // ─── <edj-wishlist-count> ────────────────────────────────────────
  class EdjWishlistCount extends HTMLElement {
    constructor() {
      super();
      this._onUpdate = this._onUpdate.bind(this);
    }

    connectedCallback() {
      this._refresh();
      document.addEventListener(EVENT_NAME, this._onUpdate);
    }

    disconnectedCallback() {
      document.removeEventListener(EVENT_NAME, this._onUpdate);
    }

    _onUpdate() {
      this._refresh();
    }

    _refresh() {
      const n = EdjWishlist.count();
      this.textContent = n > 0 ? n.toString() : '';
      this.hidden = n === 0;
      this.setAttribute('aria-label', n + (n > 1 ? ' favoris enregistrés' : ' favori enregistré'));
    }
  }
  customElements.define('edj-wishlist-count', EdjWishlistCount);

  // ─── <edj-favoris-list> ──────────────────────────────────────────
  class EdjFavorisList extends HTMLElement {
    constructor() {
      super();
      this._onUpdate = this._onUpdate.bind(this);
      this._fetchedHandles = new Set();
    }

    connectedCallback() {
      this._gridEl = this.querySelector('[data-edj-favoris-grid]');
      this._emptyEl = this.querySelector('[data-edj-favoris-empty]');
      this._countEl = this.querySelector('[data-edj-favoris-count]');
      this._render();
      document.addEventListener(EVENT_NAME, this._onUpdate);
    }

    disconnectedCallback() {
      document.removeEventListener(EVENT_NAME, this._onUpdate);
    }

    _onUpdate() {
      this._render();
    }

    _render() {
      const items = EdjWishlist.getAll();
      const n = items.length;

      if (this._countEl) {
        const noun = n > 1 ? 'pièces sauvegardées' : 'pièce sauvegardée';
        this._countEl.textContent = n + ' ' + noun;
      }

      if (n === 0) {
        if (this._emptyEl) this._emptyEl.hidden = false;
        if (this._gridEl) this._gridEl.hidden = true;
        return;
      }

      if (this._emptyEl) this._emptyEl.hidden = true;
      if (this._gridEl) {
        this._gridEl.hidden = false;
        const presentHandles = new Set(items.map(function (it) { return it.handle; }));
        // Remove orphans
        const cards = this._gridEl.querySelectorAll('[data-edj-favoris-card-handle]');
        for (let i = 0; i < cards.length; i++) {
          const node = cards[i];
          const h = node.getAttribute('data-edj-favoris-card-handle');
          if (!presentHandles.has(h)) node.remove();
        }
        // Add missing
        items.forEach((it) => {
          let existing = this._gridEl.querySelector('[data-edj-favoris-card-handle="' + CSS.escape(it.handle) + '"]');
          if (!existing) {
            const card = this._buildCard(it);
            if (card) {
              this._gridEl.appendChild(card);
              this._hydrateFromShopify(card, it.handle);
            }
          }
        });
      }
    }

    _buildCard(item) {
      // ─────────────────────────────────────────────────────────────
      // Itération 2026-05-05 Batch 4 :
      // Construit une carte qui réutilise EXACTEMENT la structure DOM
      // du composant universel `edj-product-card` (.edj-coll-card grid layout),
      // pour garantir un rendu visuel cohérent avec collection / accueil / search.
      // Wrapper edj-favoris-card + bouton ✕ "Retirer" superposé en top-right
      // (sans toucher au composant universel — approche additive).
      //
      // Iter 2026-05-07 Cleanup B (Item #3) :
      // + Ajout overlay quickview ATC `[data-edj-card-atc]` — auto-bind par
      //   <edj-variant-picker-modal> ouvre la modale singleton multi-variantes
      //   au clic. Le hover CSS .edj-coll-card:hover .edj-coll-card__quickview
      //   gère la visibilité.
      // + Ajout swatches couleur via _hydrateFromShopify (fetch options async).
      //   Les swatches sont injectés sur la même ligne que le prix
      //   (.edj-coll-card__prices--with-swatches) après réception des options.
      // ─────────────────────────────────────────────────────────────
      const card = document.createElement('article');
      card.className = 'edj-coll-card edj-coll-card--grid edj-coll-card--ratio-portrait edj-favoris-card';
      card.setAttribute('data-edj-favoris-card-handle', item.handle);
      card.setAttribute('data-product-handle', item.handle);
      // data-has-variants par défaut "true" — sera ajusté à l'hydratation
      // si le produit s'avère mono-variant.
      card.setAttribute('data-has-variants', 'true');

      // ── Lien média (image cliquable) — comme composant universel ──
      const mediaLink = document.createElement('a');
      mediaLink.className = 'edj-coll-card__media-link';
      mediaLink.href = '/products/' + encodeURIComponent(item.handle);
      mediaLink.setAttribute('aria-label', item.title || item.handle);

      const media = document.createElement('div');
      media.className = 'edj-coll-card__media';
      if (item.image) {
        const img = document.createElement('img');
        img.className = 'edj-coll-card__image';
        img.src = item.image;
        img.alt = item.title || item.handle;
        img.loading = 'lazy';
        media.appendChild(img);
      } else {
        const ph = document.createElement('div');
        ph.className = 'edj-coll-card__image edj-coll-card__image--fallback';
        ph.setAttribute('aria-hidden', 'true');
        media.appendChild(ph);
      }

      // Bouton ✕ "Retirer" overlay (top-right) — wrapper, pas dans le snippet universel
      const removeBtn = document.createElement('button');
      removeBtn.type = 'button';
      removeBtn.className = 'edj-favoris-card__remove';
      removeBtn.setAttribute('data-edj-favoris-remove', '');
      removeBtn.setAttribute('data-handle', item.handle);
      removeBtn.setAttribute('aria-label', 'Retirer ce produit des favoris');
      removeBtn.innerHTML = '<span aria-hidden="true">×</span>';
      removeBtn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        // Animation fade-out avant retrait
        card.classList.add('edj-favoris-card--removing');
        setTimeout(function () {
          EdjWishlist.remove(item.handle);
        }, 220);
      });
      media.appendChild(removeBtn);

      // ── Quickview ATC overlay (Iter 2026-05-07 Cleanup B) ──
      // Auto-bindé globalement par component-variant-picker-modal.js
      // (querySelector global sur [data-edj-card-atc] au click).
      // Le hover CSS .edj-coll-card:hover .edj-coll-card__quickview affiche le bouton.
      const atcBtn = document.createElement('button');
      atcBtn.type = 'button';
      atcBtn.className = 'edj-coll-card__quickview edj-coll-card__quickview--atc';
      atcBtn.setAttribute('data-edj-card-atc', '');
      atcBtn.setAttribute('data-product-handle', item.handle);
      atcBtn.setAttribute('data-product-title', item.title || '');
      atcBtn.setAttribute('data-has-variants', 'true');
      atcBtn.setAttribute('aria-label', 'Ajouter au panier — ' + (item.title || item.handle));
      atcBtn.textContent = '+ Ajouter au panier';
      media.appendChild(atcBtn);

      mediaLink.appendChild(media);
      card.appendChild(mediaLink);

      // ── Catégorie eyebrow (peuplée à l'hydratation) ──
      const category = document.createElement('div');
      category.className = 'edj-coll-card__category';
      category.setAttribute('data-edj-favoris-category', '');
      category.hidden = true;
      card.appendChild(category);

      // ── Titre (lien vers produit) ──
      const titleWrap = document.createElement('div');
      titleWrap.className = 'edj-coll-card__title';
      const titleLink = document.createElement('a');
      titleLink.href = '/products/' + encodeURIComponent(item.handle);
      titleLink.className = 'edj-coll-card__title-link';
      titleLink.setAttribute('data-edj-favoris-title', '');
      titleLink.textContent = item.title || item.handle;
      titleWrap.appendChild(titleLink);
      card.appendChild(titleWrap);

      // ── Prix + swatches couleur (structure cohérente avec snippet universel) ──
      // .edj-coll-card__prices contient :
      //   .edj-coll-card__prices-row (le prix)
      //   .edj-coll-card__swatches (injectées async par _hydrateFromShopify)
      const pricesWrap = document.createElement('div');
      pricesWrap.className = 'edj-coll-card__prices';
      pricesWrap.setAttribute('data-edj-favoris-prices', '');
      const pricesRow = document.createElement('span');
      pricesRow.className = 'edj-coll-card__prices-row';
      const price = document.createElement('span');
      price.className = 'edj-coll-card__price';
      price.setAttribute('data-edj-favoris-price', '');
      if (item.price_cents) price.textContent = formatPriceCents(item.price_cents);
      pricesRow.appendChild(price);
      pricesWrap.appendChild(pricesRow);
      card.appendChild(pricesWrap);

      return card;
    }

    /** Mapping label couleur → hex (mini fallback table — cohérent avec
     *  snippets/color-swatch-mapping.liquid côté Liquid).
     *  Si label inconnu → fallback vermillon doux. */
    _swatchHex(label) {
      const k = (label || '').toString().toLowerCase().trim();
      const map = {
        'noir': '#1a1815', 'black': '#1a1815',
        'blanc': '#f5f3ee', 'white': '#f5f3ee', 'écru': '#f5f3ee', 'ecru': '#f5f3ee',
        'gris': '#8a8580', 'grey': '#8a8580', 'gray': '#8a8580',
        'rouge': '#b04a2a', 'red': '#b04a2a', 'vermillon': '#b04a2a',
        'bleu': '#3a4a62', 'blue': '#3a4a62', 'marine': '#1f2a44', 'navy': '#1f2a44', 'indigo': '#2c3a5a',
        'vert': '#5a7a4a', 'green': '#5a7a4a',
        'jaune': '#d4b14a', 'yellow': '#d4b14a',
        'rose': '#d8a8a8', 'pink': '#d8a8a8',
        'violet': '#7a5a8a', 'purple': '#7a5a8a',
        'orange': '#cc7a3a',
        'beige': '#c8a89a', 'sable': '#d4c5a9', 'sand': '#d4c5a9',
        'marron': '#6a4a3a', 'brown': '#6a4a3a',
        'or': '#c9a14a', 'gold': '#c9a14a',
        'argent': '#b8b8b8', 'silver': '#b8b8b8',
      };
      return map[k] || '#c8a89a';
    }

    /** Injecte les swatches couleur dans la card si le produit a une option couleur multi-valeurs.
     *  Appelé depuis _hydrateFromShopify avec les options Shopify. */
    _injectSwatches(cardEl, options) {
      if (!options || !Array.isArray(options) || !cardEl.isConnected) return;
      // Cherche option couleur (FR ou EN)
      let colorOpt = null;
      for (let i = 0; i < options.length; i++) {
        const name = (options[i].name || '').toLowerCase();
        if (name.indexOf('couleur') !== -1 || name.indexOf('color') !== -1) {
          colorOpt = options[i];
          break;
        }
      }
      if (!colorOpt || !colorOpt.values || colorOpt.values.length <= 1) return;

      // Si swatches déjà injectées, skip (idempotence)
      if (cardEl.querySelector('.edj-coll-card__swatches[data-edj-favoris-swatches]')) return;

      const pricesWrap = cardEl.querySelector('[data-edj-favoris-prices]');
      if (!pricesWrap) return;

      pricesWrap.classList.add('edj-coll-card__prices--with-swatches');

      const visibleCount = 4;
      const overflow = colorOpt.values.length - visibleCount;
      const swatches = document.createElement('span');
      swatches.className = 'edj-coll-card__swatches';
      swatches.setAttribute('data-edj-favoris-swatches', '');
      swatches.setAttribute('aria-label', 'Coloris disponibles');

      const visible = colorOpt.values.slice(0, visibleCount);
      visible.forEach((v) => {
        const swatch = document.createElement('span');
        swatch.className = 'edj-coll-card__swatch';
        swatch.style.background = this._swatchHex(v);
        swatch.title = v;
        swatch.setAttribute('aria-hidden', 'true');
        swatches.appendChild(swatch);
      });
      if (overflow > 0) {
        const more = document.createElement('span');
        more.className = 'edj-coll-card__swatch-more';
        more.setAttribute('aria-hidden', 'true');
        more.textContent = '+' + overflow;
        swatches.appendChild(more);
      }
      pricesWrap.appendChild(swatches);
    }

    _hydrateFromShopify(cardEl, handle) {
      if (this._fetchedHandles.has(handle)) return;
      this._fetchedHandles.add(handle);
      fetch('/products/' + encodeURIComponent(handle) + '.js', { credentials: 'same-origin' })
        .then((r) => r.ok ? r.json() : null)
        .then((p) => {
          if (!p || !cardEl.isConnected) return;
          const titleEl = cardEl.querySelector('[data-edj-favoris-title]');
          if (titleEl && p.title) titleEl.textContent = p.title;
          const priceEl = cardEl.querySelector('[data-edj-favoris-price]');
          if (priceEl && typeof p.price === 'number') priceEl.textContent = formatPriceCents(p.price);
          // Catégorie (type ou collection)
          const catEl = cardEl.querySelector('[data-edj-favoris-category]');
          if (catEl) {
            const cat = p.type || (p.tags && p.tags.length ? p.tags[0] : '');
            if (cat) {
              catEl.textContent = cat;
              catEl.hidden = false;
            }
          }
          const imgEl = cardEl.querySelector('.edj-coll-card__image');
          if (p.featured_image) {
            if (imgEl && imgEl.tagName === 'IMG' && !imgEl.src) {
              imgEl.src = p.featured_image;
            } else if (imgEl && imgEl.tagName !== 'IMG') {
              const newImg = document.createElement('img');
              newImg.className = 'edj-coll-card__image';
              newImg.alt = p.title || handle;
              newImg.loading = 'lazy';
              newImg.src = p.featured_image;
              imgEl.replaceWith(newImg);
            }
          }

          // ── Iter 2026-05-07 Cleanup B (Item #3) ──
          // Mise à jour data-product-id sur la card + bouton ATC.
          // Permet à <edj-variant-picker-modal> de fetch les variantes correctement.
          if (p.id) {
            cardEl.setAttribute('data-product-id', String(p.id));
            const atcBtn = cardEl.querySelector('[data-edj-card-atc]');
            if (atcBtn) {
              atcBtn.setAttribute('data-product-id', String(p.id));
              if (p.title) atcBtn.setAttribute('data-product-title', p.title);
              // First available variant id
              if (p.variants && p.variants.length > 0) {
                const firstAvail = p.variants.find(function (v) { return v.available; }) || p.variants[0];
                if (firstAvail && firstAvail.id) atcBtn.setAttribute('data-variant-id', String(firstAvail.id));
              }
              // Si un seul variant default → mono-variant : ATC direct (pas modale)
              const hasOnlyDefault = (p.variants && p.variants.length === 1 &&
                p.variants[0].title === 'Default Title');
              const hasVariantsFlag = !hasOnlyDefault;
              cardEl.setAttribute('data-has-variants', String(hasVariantsFlag));
              atcBtn.setAttribute('data-has-variants', String(hasVariantsFlag));
            }
          }

          // ── Iter 2026-05-07 Cleanup B (Item #3) — swatches couleur ──
          // /products/{handle}.js retourne `options` (array of strings — pre-2024)
          // ou `options_with_values` (full objects). On reconstruit les
          // values à partir des variants pour éviter dépendance API.
          const opts = this._extractColorOptions(p);
          if (opts.length) this._injectSwatches(cardEl, opts);

          // Sync localStorage si découverte de champs manquants
          const items = EdjWishlist.getAll();
          const idx = items.findIndex(function (it) { return it.handle === handle; });
          if (idx !== -1) {
            const cur = items[idx];
            let dirty = false;
            if (!cur.title && p.title) { cur.title = p.title; dirty = true; }
            if (!cur.image && p.featured_image) { cur.image = p.featured_image; dirty = true; }
            if ((!cur.price_cents || cur.price_cents === 0) && typeof p.price === 'number') { cur.price_cents = p.price; dirty = true; }
            if (dirty) {
              window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
            }
          }
        })
        .catch(() => { /* soft-fail */ });
    }

    /** Reconstruit options_with_values à partir de la réponse /products/{handle}.js
     *  Format Shopify : p.options = [{name, position, values}] ou string array selon version.
     *  Fallback : agrège p.variants[].option1/2/3 en sets uniques par option name. */
    _extractColorOptions(p) {
      // Cas 1 : p.options est un array d'objets {name, values}
      if (Array.isArray(p.options) && p.options.length && typeof p.options[0] === 'object' && p.options[0] !== null) {
        return p.options;
      }
      // Cas 2 : p.options = ['Couleur', 'Taille'] (strings) + p.variants[].option1/2/3
      if (Array.isArray(p.options) && p.options.length && typeof p.options[0] === 'string' && Array.isArray(p.variants)) {
        const result = [];
        p.options.forEach((name, idx) => {
          const key = 'option' + (idx + 1);
          const set = new Set();
          p.variants.forEach((v) => {
            if (v[key]) set.add(v[key]);
          });
          result.push({ name: name, position: idx + 1, values: Array.from(set) });
        });
        return result;
      }
      return [];
    }
  }
  customElements.define('edj-favoris-list', EdjFavorisList);
})();
